import React, { useEffect, useMemo, useState } from "react";
import { Container, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useDebouncedSearch } from "@utils/hooks/useDebouncedSearch";
import { useGetServiceCentersNuskQuery } from "@services/serviceCenters";
import { useEmployeesOptionsQuery } from "@/services/basicData/basicData";
import { LabelValue } from "@/Component/Value";

const ServiceCenters = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [employeesModalOpen, setEmployeesModalOpen] = useState(false);
  const [selectedCenterName, setSelectedCenterName] = useState("");
  const [selectedCenterEmployees, setSelectedCenterEmployees] = useState([]);

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const normalizedSearch = debouncedValue.trim();
  const { data: employeesRes } = useEmployeesOptionsQuery();
  const employees = employeesRes?.data?.data?.data ?? employeesRes?.data?.data ?? [];

  const { data: res, isLoading, isFetching } = useGetServiceCentersNuskQuery({
    page,
    per_page: 10,
    ...(normalizedSearch ? { search: normalizedSearch } : {}),
    ...filters,
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedValue, filters]);

  const serviceCenters = res?.data?.data?.data ?? res?.data?.data ?? [];
  const meta = res?.data?.data?.meta ?? res?.data?.meta;
  const loading = isLoading || isFetching;

  const openEmployeesModal = (row) => {
    const employeesList = row?.service_center?.employees ?? [];
    if (!employeesList.length) return;

    setSelectedCenterEmployees(employeesList);
    setSelectedCenterName(
      row?.service_center?.name_ar ??
        row?.service_center?.name_en ??
        row?.service_center?.name ??
        ""
    );
    setEmployeesModalOpen(true);
  };

  const closeEmployeesModal = () => {
    setEmployeesModalOpen(false);
    setSelectedCenterEmployees([]);
    setSelectedCenterName("");
  };

  const columns = useMemo(
    () => [
      {
        name: t("SERVICE_CENTER_CODE"),
        key: "code",
        render: (value) => (
          <LabelValue value={value} copyValue={value} buttonIconSize={12} />
        ),
      },
      {
        name: t("SERVICE_CENTER"),
        key: "service_center_name",
        render: (_, row) =>
          row?.serviceCenterNameAr ??
          row?.serviceCenterNameEn ??
          row?.service_center?.name_ar ??
          row?.service_center?.name_en ??
          row?.service_center?.name ??
          "-",
      },
      {
        name: t("EMPLOYEE_NAME"),
        key: "employee_name",
        render: (_, row) =>
          row?.service_center?.manager?.name_ar ??
          row?.service_center?.manager?.name_en ??
          "-",
      },
      {
        name: t("NATIONAL_ID"),
        key: "national_id",
        render: (_, row) =>
          row?.service_center?.manager?.national_id ??
          "-",
      },
      {
        name: t("PHONE"),
        key: "phone",
        render: (_, row) =>
          row?.service_center?.manager?.phone ??
          "-",
      },
      {
        name: t("EMAIL"),
        key: "email",
        render: (_, row) =>
          row?.service_center?.manager?.email ??
          "-",
      },
      {
        name: t("SERVICE_CENTER_MEMBERS"),
        key: "members_count",
        render: (_, row) => {
          const count = row?.service_center?.employees?.length ?? 0;
          if (!count) return "-";

          return (
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-underline"
              onClick={() => openEmployeesModal(row)}
            >
              {`${count} employees`}
            </button>
          );
        },
      },
    ],
    [t]
  );

  const filterFields = [
    {
      colSize: { md: 6 },
      name: "code",
      label: t("SERVICE_CENTER_CODE"),
      type: "input",
    },
    {
      colSize: { md: 6 },
      name: "manager_id",
      label: t("RESPONSIBLE_EMPLOYEE"),
      type: "select",
      placeholder: t("SELECT_RESPONSIBLE_EMPLOYEE"),
      options: employees.map((employee) => ({
        value: employee.id,
        label:
          [
            employee.id,
            employee.name ?? employee.name_ar ?? employee.name_en,
            employee.phone,
          ]
            .filter(Boolean)
            .join(" - ") || employee.email,
      })),
    },
  ];

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
  };

  const handleFilterReset = () => {
    setFilters({});
  };

  return (
    <div>
      <PageHeader title={t("SERVICE_CENTERS")} sticky={false} />
      <Container fluid>
        <TableC
          data={serviceCenters}
          columns={columns}
          loading={loading}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t("SERVICE_CENTER_SEARCH_PLACEHOLDER")}
          pagination={{
            current: page,
            total: meta?.total ?? 0,
            pageSize: meta?.per_page ?? 10,
            onChange: (p) => setPage(p),
          }}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          emptyStateTitle={t("SERVICE_CENTER_NO_DATA")}
          emptyStateNoResultsTitle={t("SERVICE_CENTER_NO_RESULTS")}
          skeletonCount={5}
        />
      </Container>

      <Modal isOpen={employeesModalOpen} toggle={closeEmployeesModal} size="lg" centered>
        <ModalHeader toggle={closeEmployeesModal}>
          {selectedCenterName
            ? `${selectedCenterName} - ${selectedCenterEmployees.length} employees`
            : `${selectedCenterEmployees.length} employees`}
        </ModalHeader>
        <ModalBody>
          <Table responsive hover>
            <thead>
              <tr>
                <th>{t("EMPLOYEE")}</th>
                <th>{t("NATIONAL_ID")}</th>
                <th>{t("PHONE")}</th>
                <th>{t("EMAIL")}</th>
              </tr>
            </thead>
            <tbody>
              {selectedCenterEmployees.length ? (
                selectedCenterEmployees.map((employee) => (
                  <tr key={employee?.id}>
                    <td>{employee?.name_ar ?? employee?.name_en ?? "-"}</td>
                    <td>{employee?.national_id ?? "-"}</td>
                    <td>{employee?.phone ?? "-"}</td>
                    <td>{employee?.email ?? "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    {t("NO_DATA") || "No data"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ServiceCenters;
